import React, { useEffect, useState } from 'react';

import styled from 'styled-components';
import { ControlLabel, Form, FormControl, FormGroup } from 'react-bootstrap';

import GroupedFormField from '../../../utils/FormWidgets/FormGroup';
import ActivityViewDeepEditableField from '../../ActivityView/ActivityViewDeepEditableField';
import ActivityAvailableDays from '../../ActivityMetaFields/ActivityAvailableDays/ActivityAvailableDays';
import ActivitiesTagsForm from '../../ActivityTagFields/ActivitiesTagsForm';
import SimpleSelect from '../../../utils/FormWidgets/SimpleSelect';
import { CANCELLATION_POLICY_OPTIONS } from '../../../../infrastructure/constants/activities';
import { ActivitiesSourceForm } from '../../ActivitySourceFields/ActivitiesSourceForm';
import {
  Activity,
  ActivitySource,
  ActivitySourceValue,
  BookingOption,
} from '../../../../types/Activity';
import { StripeAccount } from '../../../../types/Stripe';
import CategorySelector from '../../ActivityView/CategorySelector';
import ActivitiesAutoComplete from '../../../utils/AutoComplete/ActivitiesAutoComplete';
import * as ActivityService from '../../../../services/ActivitiesService';
import ActivityFormMultiSelector from '../../ActivityForm/ActivityFormMultiSelector/ActivityFormMultiSelector';
import { PartyType } from '../../../../types/Party';
import CapabilitiesSection from '../../CapabilitiesSection';
import CitySelector from '../../ActivityMetaFields/CitySelector';
import { isFareharborSource } from '../../../forms/EditPeekAndRedeamOrderForm/helpers';
import VendorsAutoComplete from '../../../utils/AutoComplete/VendorsAutoComplete';

const StyledWrapper = styled(Form)`
  border-top: 1px solid #d9d9d9;
  padding: 10px;
  padding-top: 20px;
`;

type Props = {
  allTags: string[];
  bookingOptionsData: Partial<BookingOption>[];
  fieldsValue: Partial<Activity>;
  showActivitySelector?: boolean;
  onActivitySelect(activity: Activity | undefined): void;
  onChange(fieldKey: string, value: unknown): void;
  onChangeCategory: (categoryId: string, subCategoryId: string) => void;
  onFormSubmit(): void;
  stripeAccounts: StripeAccount[];
  existingSource?: ActivitySourceValue;
  isNotIntegrationEnabled?: boolean;
  isBookingPlatformIntegrationAbsent: boolean;
  partyTypes: PartyType[];
};

const renderActivityField = (
  isNotIntegrationEnabled: boolean,
  children: React.ReactNode
) => {
  if (isNotIntegrationEnabled) {
    return null;
  }
  return children;
};

const ActivityCreateModalContent = ({
  allTags,
  bookingOptionsData,
  fieldsValue,
  showActivitySelector,
  onActivitySelect,
  onChange,
  onChangeCategory,
  onFormSubmit,
  stripeAccounts,
  existingSource,
  isNotIntegrationEnabled,
  partyTypes,
  isBookingPlatformIntegrationAbsent,
}: Props) => {
  const [selectedActivityId, setSelectedActivityId] = useState<string>();
  const [selectedActivity, setSelectedActivity] = useState<Activity>();
  const [commissionFieldText, setCommissionFieldText] = useState<string>(
    fieldsValue.commission ? fieldsValue.commission.toFixed(2) : ''
  );
  const [commissionFieldError, setCommissionFieldError] = useState<string>('');
  console.log(fieldsValue);
  const [selectedPartyTypes, setSelectedPartyTypes] = useState<string[]>([]);
  const [availablePartyTypes, setAvailablePartyTypes] = useState<string[]>(
    partyTypes.map(t => t.id)
  );

  useEffect(() => {
    if (!selectedActivityId) {
      setSelectedActivity(undefined);
      return;
    }

    ActivityService.getById(selectedActivityId).then(setSelectedActivity);
  }, [selectedActivityId]);

  useEffect(() => {
    // ensures we always include supplierId field for experiences relying on integration partners
    const updatedActivity = selectedActivity;
    if (!updatedActivity) return;
    if (
      selectedActivity &&
      !selectedActivity.source?.includes('bach') &&
      !selectedActivity.supplierId
    ) {
      updatedActivity.supplierId = fieldsValue.supplierId;
    }
    onActivitySelect(updatedActivity);
  }, [fieldsValue.supplierId, onActivitySelect, selectedActivity]);

  const onVendorChange = id => {
    const { metadata } = fieldsValue;
    onChange('vendorId', id);
    onChange('metadata', { ...metadata, stripeId: undefined });
  };
  const onCommissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommissionFieldText(e.target.value);

    const parsedCommission = Number(e.target.value);
    if (Number.isNaN(parsedCommission)) {
      setCommissionFieldError('Commission should be a valid decimal value');
      return;
    }

    if (parsedCommission < 0 || 100 < parsedCommission) {
      setCommissionFieldError('Commission should be between 0 and 100');
      return;
    }

    setCommissionFieldError('');
    onChange('commission', parsedCommission);
  };
  const onPartyTypesChange = (value: string) => {
    let partyTypes: string[] = [];
    if (selectedPartyTypes.includes(value)) {
      partyTypes = selectedPartyTypes.filter(t => t !== value);
      setAvailablePartyTypes([...availablePartyTypes, value]);
    } else {
      partyTypes = Array.from(new Set([...selectedPartyTypes, value]));
      setAvailablePartyTypes(availablePartyTypes.filter(t => t !== value));
    }

    onChange('partyTypes', partyTypes);
    setSelectedPartyTypes(partyTypes);
  };

  const { meta, metadata } = fieldsValue ?? {};

  return (
    <StyledWrapper onSubmit={onFormSubmit}>
      {showActivitySelector && (
        <FormGroup>
          <ControlLabel>Activity</ControlLabel>
          <ActivitiesAutoComplete onSelect={setSelectedActivityId} clearable />
        </FormGroup>
      )}
      <GroupedFormField
        title='Title*'
        placeholder='Title'
        onChange={event => onChange('title', event.target.value)}
        value={fieldsValue.title}
      />
      <FormGroup>
        <ControlLabel>Vendor*</ControlLabel>
        {<VendorsAutoComplete onSelect={onVendorChange} />}
      </FormGroup>
      <CategorySelector activity={fieldsValue} onSave={onChangeCategory} />

      <FormGroup>
        <ControlLabel>Description</ControlLabel>
        {
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          <ActivityViewDeepEditableField
            activity={fieldsValue}
            fieldsInEditMode={['description']}
            name='description'
            setParentState={value => onChange('description', value)}
          />
        }
      </FormGroup>
      <ActivitiesTagsForm
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        selectedTags={fieldsValue.tags}
        allTags={allTags}
        onChange={value => onChange('tags', value)}
        disabled={
          !isFareharborSource(
            existingSource ? ActivitySource[existingSource] : undefined
          )
        }
      />

      <ActivityFormMultiSelector
        labelStyle={{ width: '12%' }}
        label='Party Types'
        caption={'Applied Party Types'}
        selectedItems={selectedPartyTypes}
        options={availablePartyTypes}
        onItemClick={onPartyTypesChange}
      />

      <GroupedFormField
        title='Popularity'
        placeholder='Popularity'
        onChange={event => onChange('popularity', +event.target.value)}
        value={fieldsValue.popularity}
      />

      {
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        <GroupedFormField
          title='Booking Options'
          placeholder='Booking Options'
          value={bookingOptionsData.map(o => o.title).join(', ')}
        />
      }
      <CitySelector
        city={fieldsValue.city}
        handleChangeCity={city => onChange('city', city)}
      />
      {renderActivityField(
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        isNotIntegrationEnabled,
        <ActivityAvailableDays
          label='Availability'
          value={fieldsValue.hours}
          initialValue={fieldsValue.hours}
          onMetaFieldsChange={values => onChange('hours', values[0].value)}
          display='block'
        />
      )}
      <GroupedFormField
        title='Booking Cutoff (Input a number of days. If no value is input, default is 7)'
        placeholder='Booking Cutoff'
        disabled={!isBookingPlatformIntegrationAbsent}
        onChange={event => onChange('deadline', +event.target.value * 24)}
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        value={
          typeof fieldsValue.deadline !== 'undefined'
            ? +fieldsValue.deadline / 24
            : undefined
        }
      />
      <FormGroup>
        <ControlLabel>Cancellation Policy</ControlLabel>
        <SimpleSelect
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          onChange={value => onChange('cancellationPolicy', value)}
          value={
            selectedActivity?.cancellationPolicy ||
            fieldsValue.cancellationPolicy
          }
          disabled={!!selectedActivity}
          data={[
            {
              label: 'Generous',
              value: CANCELLATION_POLICY_OPTIONS.get('Generous'),
            },
            {
              label: 'Moderate',
              value: CANCELLATION_POLICY_OPTIONS.get('Moderate'),
            },
            {
              label: 'Strict',
              value: CANCELLATION_POLICY_OPTIONS.get('Strict'),
            },
          ]}
        />
      </FormGroup>
      <GroupedFormField
        title='Bach Tip'
        placeholder='Bach Tip'
        onChange={event => onChange('bachTip', event.target.value)}
        value={fieldsValue.bachTip}
      />
      <GroupedFormField
        title='FAQ Content'
        placeholder='FAQ Content'
        onChange={event => onChange('faqContent', event.target.value)}
        value={fieldsValue.faqContent}
      />
      <ActivitiesSourceForm
        stripeAccounts={stripeAccounts}
        // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
        // @ts-ignore
        commission={fieldsValue.commission}
        metadata={fieldsValue.metadata}
        onChange={onChange}
        existingSource={existingSource}
        showErrors={true}
        source={fieldsValue.source}
        deadline={fieldsValue.deadline}
        disabled={
          !isFareharborSource(
            existingSource ? ActivitySource[existingSource] : undefined
          )
        }
      />
      <FormGroup className={`${commissionFieldError && 'has-error'}`}>
        <ControlLabel>Commission*</ControlLabel>
        <FormControl
          type={'text'}
          placeholder={'Commission'}
          value={commissionFieldText}
          onChange={onCommissionChange}
        />
        {commissionFieldError && (
          <span className={'help-block'}>{commissionFieldError}</span>
        )}
      </FormGroup>
      <CapabilitiesSection
        metadata={metadata}
        meta={meta}
        onSave={({ meta }) => {
          onChange('meta', meta);
        }}
        stripeConnectedAccounts={stripeAccounts}
        disabled={
          !isFareharborSource(
            existingSource ? ActivitySource[existingSource] : undefined
          )
        }
      />
    </StyledWrapper>
  );
};

export default ActivityCreateModalContent;
